/**
 * Subscription revalidation for cart and checkout (VOY-610).
 *
 * The cart re-checks special-priced items against the user's plan. A FAILED lookup says
 * nothing about the plan, so it must neither revert items to standard prices nor block
 * checkout as if the user were Free; only a successful "no qualifying plan" answer does.
 * The cart-checkout edge function re-quotes with the server-side tier either way.
 */

import { fetchCurrentSubscription } from './stripe';
import { resolvePricingRules } from './pricing-config';
import type { PricingRules } from './subscription-rules';
import type { CartPriceType } from '@/types/cart';

/**
 * Pricing rules for the user's current plan, or null when the plan is unknown (lookup failed,
 * or it was answered for a different session than `userId`).
 */
export async function resolveRevalidationRules(userId: string | null): Promise<PricingRules | null> {
  const result = await fetchCurrentSubscription(userId);
  if (!result.ok) {
    console.warn('[subscription] Plan unknown, skipping special-price revalidation:', result.error);
    return null;
  }
  return resolvePricingRules(result.subscription);
}

/** True only when the plan is known and does not allow special prices. */
export function shouldRevertSpecialPrice(
  priceType: CartPriceType | undefined,
  rules: PricingRules | null
): boolean {
  return priceType === 'special' && rules !== null && !rules.canHaveSpecialPrice;
}
